import { useEffect, useState } from 'react';
import isFunction from 'lodash/isFunction';
import { Identifiable, OvColumn } from '../rebalancePage/sharedTypes';
import useTableColumnDragDrop from './useTableColumnDragDrop';
import useTableColumnVisibility from './useTableColumnVisibility';
import useTablePin from './useTablePin';
import useTableSort from './useTableSort';
import useTablePagination from './useTablePagination';

import {
  Table,
  TableRow,
  TableCell,
  TableHeadCell, Pagination,
  Checkbox,
} from '../../../2-component';

import { Box, Skeleton } from '../../../1-primative';
import useSelectionModel from './useSelectionModel';

type TableProps<T> = {
  data: T[];
  columns: OvColumn<T>[];
  loading: boolean;
  initialPage: number;
  linesPerPage: number;
  totalLines: number;
  checkbox: boolean;
  pagination: boolean;
  onPage?: (page: number, itemsPerPage: number) => void;
  onSort?: (column: keyof T | null, direction: 'asc' | 'desc' | undefined) => void;
  onSelect?: (data: T[]) => void;
  onTableRowClick?: (row: T) => void;
};

const OvTable = <T extends Identifiable>({
  data, columns: initialColumns, loading, initialPage, linesPerPage, totalLines, checkbox, pagination, onPage, onSort, onSelect, onTableRowClick,
}: TableProps<T>) => {
  const [currentColumns, setCurrentColumns] = useState<OvColumn<T>[]>(initialColumns);

  const initialSortColumnDirection = (): {
    column: keyof T | null, direction: 'asc' | 'desc' | undefined
  } | undefined => {
    const sortedColumn = currentColumns.find(
      (column) => column.sorted === 'asc' || column.sorted === 'desc',
    );
    if (sortedColumn && sortedColumn.sorted) {
      return { column: sortedColumn.accessor, direction: sortedColumn.sorted };
    }
    return undefined;
  };

  const {
    columns: draggableColumns,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useTableColumnDragDrop<T>(currentColumns);

  const {
    sortColumn,
    sortDirection,
    handleSort,
    sortedColumns,
  } = useTableSort<T>(draggableColumns, initialSortColumnDirection()?.column, initialSortColumnDirection()?.direction);

  const {
    visibleColumns,
    updateColumnVisibility,
  } = useTableColumnVisibility<T>(sortedColumns);

  const {
    partitionedColumns,
  } = useTablePin<T>(visibleColumns);

  const {
    currentPage,
    totalPages,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    setTotalItems,
  } = useTablePagination(initialPage, totalLines);

  const {
    toggle,
    isSelected,
    clear,
    setSelection,
    selected,
  } = useSelectionModel<T>([], true, (item: T) => item.id);

  useEffect(() => {
    setCurrentColumns(initialColumns);
    updateColumnVisibility(initialColumns);
  }, [initialColumns, updateColumnVisibility]);

  useEffect(() => {
    if (onPage) onPage(currentPage, itemsPerPage);
  }, [currentPage, itemsPerPage, onPage]);

  useEffect(() => {
    if (onSort) onSort(sortColumn, sortDirection);
  }, [sortColumn, sortDirection, onSort]);

  useEffect(() => {
    if (onSelect) { onSelect(selected); }
  }, [selected, onSelect]);

  useEffect(() => {
    setItemsPerPage(linesPerPage);
    setTotalItems(totalLines);
  }, [totalLines, linesPerPage, setItemsPerPage, setTotalItems]);

  const getRowKey = (row: T, index: string | number) => `${row.id}-${index}`;

  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <Table>
          <TableRow>
            { checkbox && <TableHeadCell padding='none'>
              <Box display='flex' alignItems='center' justifyContent='center'>
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < data.length}
                  checked={selected.length === data.length && data.length > 0}
                  onChange={(checked: boolean) => {
                    if (checked) {
                      setSelection(data);
                    } else {
                      clear();
                    }
                  }}
                  customStyle={{
                    margin: 0,
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              </Box>
            </TableHeadCell> }
            {partitionedColumns.leftColumns.map((column) => (
              <TableHeadCell
                hidden={!column.visible}
                isSortable={column.sort}
                sortDirection={column.sorted || false}
                key={column.accessor.toString()}
                draggable
                onDragStart={(e) => handleDragStart(e, column.accessor)}
                onDragOver={(e) => handleDragOver(e, column.accessor)}
                onDrop={(e) => handleDrop(e, column.accessor)}
                onDragEnd={handleDragEnd}
                onClick={() => {
                  if (!column.sort) { return; }
                  handleSort(column.accessor);
                  setCurrentPage(1);
                }}>
                {isFunction(column.header) ? column.header(column) : column.header}
              </TableHeadCell>
            ))}
            {partitionedColumns.normalColumns.map((column) => (
              <TableHeadCell
                hidden={!column.visible}
                isSortable={column.sort}
                sortDirection={column.sorted || false}
                key={column.accessor.toString()}
                draggable
                onDragStart={(e) => handleDragStart(e, column.accessor)}
                onDragOver={(e) => handleDragOver(e, column.accessor)}
                onDrop={(e) => handleDrop(e, column.accessor)}
                onDragEnd={handleDragEnd}
                onClick={() => {
                  if (!column.sort) { return; }
                  handleSort(column.accessor);
                  setCurrentPage(1);
                }}>
                {isFunction(column.header) ? column.header(column) : column.header}
              </TableHeadCell>
            ))}
            {partitionedColumns.rightColumns.map((column) => (
              <TableHeadCell
                hidden={!column.visible}
                isSortable={column.sort}
                sortDirection={column.sorted || false}
                key={column.accessor.toString()}
                draggable
                onDragStart={(e) => handleDragStart(e, column.accessor)}
                onDragOver={(e) => handleDragOver(e, column.accessor)}
                onDrop={(e) => handleDrop(e, column.accessor)}
                onDragEnd={handleDragEnd}
                onClick={() => {
                  if (!column.sort) { return; }
                  handleSort(column.accessor);
                  setCurrentPage(1);
                }}>
                {isFunction(column.header) ? column.header(column) : column.header}
              </TableHeadCell>
            ))}
          </TableRow>

          { !loading && !data.length && (<TableRow>
            <TableCell colSpan={checkbox ? visibleColumns.length + 1 : visibleColumns.length}>
              No results found
            </TableCell>
          </TableRow>)}

          { loading && Array.from({ length: linesPerPage }, (_, index: number) => (
            <TableRow key={index}>
              {Array.from({ length: checkbox ? visibleColumns.length + 1 : visibleColumns.length }, (__, indexCel: number) => (
                <TableCell key={`cell-${indexCel}`}>
                  <Skeleton width='100%' />
                </TableCell>
              ))}
            </TableRow>
          ))}

          {!loading && data.length && data.map((row, index) => (
            <TableRow key={getRowKey(row, index)} onClick={(event) => {
              event?.preventDefault();
              event.stopPropagation();
              if (onTableRowClick) { onTableRowClick(row); }
            }}>
              { checkbox && <TableCell padding='checkbox'>
                <Box display='flex' alignItems='center' justifyContent='center'>
                  <Checkbox
                    checked={isSelected(row)}
                    onChange={() => toggle(row)}
                    customStyle={{
                      margin: 0,
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  />
                </Box>
              </TableCell> }
              {partitionedColumns.leftColumns.map((column) => (
                <TableCell hidden={!column.visible} key={column.accessor.toString()}>
                  {isFunction(column.cell) ? column.cell(row) : row[column.accessor]}
                </TableCell>
              ))}
              {partitionedColumns.normalColumns.map((column) => (
                <TableCell hidden={!column.visible} key={column.accessor.toString()}>
                  {isFunction(column.cell) ? column.cell(row) : row[column.accessor]}
                </TableCell>
              ))}
              {partitionedColumns.rightColumns.map((column) => (
                <TableCell hidden={!column.visible} key={column.accessor.toString()}>
                  {isFunction(column.cell) ? column.cell(row) : row[column.accessor]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </Table>
      </Box>

      { pagination && <Pagination
        count={totalPages}
        page={currentPage}
        onChange={(_e, newPage) => setCurrentPage(newPage)}
        sx={{
          p: 1,
          textAlign: 'right',
          '.MuiPagination-ul': { justifyContent: 'end' },
        }}
      /> }
    </>
  );
};

export default OvTable;
